import { useEffect, useState } from 'react';
import { OrganisationFeatureFlags } from 'shared';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Navigate, Outlet } from 'react-router';

import { Card } from '../../../common/Atoms/Card';
import { ITab, Tabs } from '../../../common/Atoms/Tabs';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';

const AGENTS_EXPLAINER_DISMISSED_KEY_PREFIX = `agents_explainer_dismissed_`;

export function AgentsRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const aiAgentEnabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_AGENT);
  const [isExplainerDismissed, setIsExplainerDismissed] = useState(false);

  if (!aiAgentEnabled) {
    return <Navigate
      to={ `..` }
      replace />;
  }

  const orgScopedExplainerKey = `${AGENTS_EXPLAINER_DISMISSED_KEY_PREFIX}${currentOrg?.id || `unknown`}`;

  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem(orgScopedExplainerKey);
      setIsExplainerDismissed(dismissed === `true`);
    }
    catch {
      setIsExplainerDismissed(false);
    }
  }, [orgScopedExplainerKey]);

  const tabs: ITab[] = [
    {
      name: `Conversations`,
      href: `conversations`,
    },
    // {
    //   name: `Rules`,
    //   href: `rules`,
    // },
    {
      name: `Agent Settings`,
      href: `agent-settings`,
    },
  ];

  return (
    <Card>
      {
        !isExplainerDismissed &&
        <div className={ `mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4` }>
          <div className={ `flex items-start justify-between gap-3` }>
            <div>
              <Paragraph className={ `mb-2 text-blue-900` }>
                { `What Agents are for` }
              </Paragraph>
              <Paragraph
                size={ `sm` }
                className={ `mb-2 text-blue-900` }
              >
                { `Agents help your team handle customer invoice conversations faster by combining AI suggestions with your approvals when needed.` }
              </Paragraph>
              <ul className={ `mb-0 pl-5 text-sm text-blue-900` }>
                <li>{ `Create and track conversations from customer replies` }</li>
                <li>{ `Draft or send customer messages and statements` }</li>
                <li>{ `Ask for approval before sensitive actions` }</li>
                <li>{ `Use prompts to control agent behavior and priorities` }</li>
              </ul>
            </div>
            <button
              type={ `button` }
              className={ `shrink-0 rounded-sm p-1 text-blue-700 hover:bg-blue-100` }
              aria-label={ `Dismiss agents explainer` }
              onClick={ () => {
                try {
                  sessionStorage.setItem(orgScopedExplainerKey, `true`);
                }
                catch {
                  // Silently fail if sessionStorage is not available
                }

                setIsExplainerDismissed(true);
              } }
            >
              <XMarkIcon className={ `h-5 w-5` } />
            </button>
          </div>
        </div>
      }

      <Tabs
        tabs={ tabs }
      />

      <Outlet />
    </Card>
  );
}
